/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Column,
  ColumnFiltersState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import React, { useEffect, useMemo, useState } from 'react';
import ChevronDown from '../../assets/chevron-down.svg?react';

import dayjs from 'dayjs';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { addOrRemove, getCurrencyStringValue } from 'shared';
import ResetIcon from '../../assets/delete.svg?react';
import FilterIcon from '../../assets/filter.svg?react';
import SaveIcon from '../../assets/save.svg?react';
import { Checkbox } from '../Checkbox/Checkbox';
import { Dropdown } from '../Dropdown/Dropdown';
import './Table.scss';
import { TableComponentProps, TableHeader, TableRow } from './Table.types';

const COLUMN_TYPE = 'COLUMN';

/**
 * Helper model for react dnd
 */
type DragItem = {
  index: number;
  id: string;
  type: string;
};

/**
 * Render gate to decide if the complete table should be rendered or a
 * fallback component. This is needed to minimize the rerender logic
 */
export const Table: React.FC<TableComponentProps> = ({
  rows,
  header = [],
  headless,
  hidePagination,
  paginationConfig = {
    pageSize: 25,
  },
  filterValue = '',
  tableConfig,
  updateTableConfig,
  selectAll,
  selectAllLabel,
  allSelecteded,
  hideTopFilter,
}) => {
  const [visibleChilds, setVisibleChilds] = useState<string[]>([]);

  const renderOutput: React.JSX.Element = useMemo((): React.JSX.Element => {
    if (rows.length > 0)
      return (
        <InternalTable
          visibleChilds={visibleChilds}
          updateTableConfig={updateTableConfig}
          rows={rows}
          header={tableConfig ? tableConfig : header}
          paginationConfig={paginationConfig}
          filterValue={filterValue}
          tableConfig={tableConfig}
          selectAll={selectAll}
          selectAllLabel={selectAllLabel}
          allSelecteded={allSelecteded}
          hideTopFilter={hideTopFilter}
          headless={headless}
          setVisibleChilds={setVisibleChilds}
          hidePagination={hidePagination}
        />
      );
    return <h2>no data :(</h2>;
  }, [
    allSelecteded,
    hidePagination,
    headless,
    visibleChilds,
    filterValue,
    header,
    hideTopFilter,
    paginationConfig,
    rows,
    selectAll,
    selectAllLabel,
    tableConfig,
    updateTableConfig,
  ]);

  return renderOutput;
};

const Filter = ({ column }: { column: Column<any, unknown> }) => {
  const columnFilterValue = column.getFilterValue();

  //Hook to set the filter if the parent path is the same or remove it if the path has changed
  useEffect(() => {
    //If we are on edit page like user/edit or crm/edit we dont want to load or delete it
    if (location.pathname.split('/').length > 2) return;
    //Cheak if filter for current page should be set or removed
    const header: string = column.columnDef.header as string;
    const filters = JSON.parse(localStorage.getItem('tableFilter') || '{}');
    const locationPath: string = location.pathname.split('/')[1];
    if (filters.path !== locationPath) localStorage.removeItem('tableFilter');
    else column.setFilterValue(filters[header]);
  }, [column]);

  return (
    <input
      value={
        typeof columnFilterValue === 'string'
          ? columnFilterValue
          : typeof columnFilterValue === 'number'
            ? columnFilterValue.toString()
            : ''
      }
      onChange={(e) => {
        if (!column.columnDef.header) return;
        const header: string = column.columnDef.header as string;
        const filterValue: string = e.target.value;
        const existingFilter: any = JSON.parse(localStorage.getItem('tableFilter') || '{}');
        existingFilter[header] = filterValue;
        existingFilter['path'] = location.pathname.split('/')[1];
        localStorage.setItem('tableFilter', JSON.stringify(existingFilter));
        column.setFilterValue(filterValue);
      }}
      placeholder={`Filter`}
    />
  );
};

/**
 * The _actual_ table component
 */
const InternalTable: React.FC<TableComponentProps> = ({
  rows,
  header = [],
  filterValue = '',
  paginationConfig,
  tableConfig,
  updateTableConfig,
  selectAll,
  selectAllLabel,
  allSelecteded,
  visibleChilds,
  setVisibleChilds,
  hidePagination,
  headless,
  hideTopFilter,
}) => {
  const [showFilter, toggleFilter] = useState<boolean>(false);
  const [localHeader, setLocalHeader] = useState<TableHeader[]>(structuredClone(header));
  const [updatedRows, setUpdatedRows] = useState<TableRow[]>(rows);

  /**
   * Hook to trigger a rerender everytime the rows change
   */
  useEffect(() => {
    setUpdatedRows(rows);
  }, [rows]);

  /**
   * Hook to check for header configuration for the active table based on the headers
   */
  useEffect(() => {
    if (!tableConfig) {
      if (header.length === localHeader.length) return; // Previously responsible for loosing focus on input
      setLocalHeader(header);
      return;
    }

    const selectedHeaders: TableHeader[] = tableConfig;
    if (selectedHeaders.length === localHeader.length) return; // Previously responsible for loosing focus on input
    setLocalHeader(selectedHeaders);
  }, [header, localHeader.length, rows, tableConfig]);

  /**
   * Holds the react table columns to display
   */
  const columns = React.useMemo(() => {
    if (!rows.length) return [];
    const columnHelper = createColumnHelper<TableRow>();

    const localCols = localHeader.map((h, index) => {
      return columnHelper.accessor(
        (row) => {
          if (!row.content[index]) return '';
          else if (typeof row.content[index] === 'object' && 'filterValue' in row.content[index])
            return row.content[index].filterValue;
          else return row.content[index];
        },
        {
          header: h.text,
          filterFn: (row, _, searchValue) => {
            if (row.original.content[index] instanceof Element || !row.original.content[index])
              return false;
            const filterValue: string =
              typeof row.original.content[index] === 'object' &&
              'filterValue' in row.original.content[index]
                ? String(row.original.content[index].filterValue)
                : String(row.original.content[index]);

            return String(filterValue).toLowerCase().includes(searchValue.toLowerCase());
          },
          cell: (row) => {
            if (!row.row.original?.content[index]) return '';
            else if (
              typeof row.row.original?.content[index] === 'object' &&
              'filterValue' in row.row.original.content[index]
            )
              return row.row.original?.content[index].value;
            else return row.row.original?.content[index];
          },
          size: h.growFactor,
          footer: (column) => {
            if (localHeader[index].showSum) {
              let sum: number = 0;
              const rows = column.table.getRowModel().rows;

              rows.forEach((row) => {
                const value: unknown = row.getValue(column.column.id);

                //Check if value is a number
                if (typeof value === 'number') {
                  sum += value;

                  //Check if value can be converted to a number
                } else if (!isNaN(Number(value))) {
                  sum += Number(value);
                  // Check for currencyValues, that are containng the currencyValue, a space and then the currencySymbol
                } else if (typeof value === 'string' && !isNaN(Number(value.split(' ')[0]))) {
                  sum += Number(value.split(' ')[0]);
                  //At last, check for an input containing a number
                } else if (typeof value === 'object') {
                  if (
                    value !== null &&
                    'props' in value &&
                    typeof value.props === 'object' &&
                    value.props !== null &&
                    'value' in value.props &&
                    !isNaN(Number(value.props.value))
                  )
                    sum += Number(value.props.value);
                }
              });

              return sum === 0 ? '' : getCurrencyStringValue(sum);
            }
            return '';
          },
          id: `column_${index}_${h.text}`,
          enableColumnFilter: true,
          sortingFn: (rowA, rowB) => {
            const valueA = rowA.getValue(`column_${index}_${h.text}`);
            const valueB = rowB.getValue(`column_${index}_${h.text}`);
            if (typeof valueA === 'number' && typeof valueB === 'number') return valueA - valueB;

            if (typeof valueA !== 'string' || typeof valueB !== 'string') return 0;
            if (
              typeof valueA === 'string' &&
              !isNaN(Number(valueA.split(' ')[0])) &&
              typeof valueB === 'string' &&
              !isNaN(Number(valueB.split(' ')[0]))
            ) {
              return Number(valueA.split(' ')[0]) - Number(valueB.split(' ')[0]);
            }
            if (h.sortByDate) {
              const datesA: string[] = valueA.split('.');
              const dateA = dayjs()
                .set('date', Number(datesA[0]))
                .set('month', Number(datesA[1]) - 1)
                .set('year', Number(datesA[2]));
              const datesB: string[] = valueB.split('.');
              const dateB = dayjs()
                .set('date', Number(datesB[0]))
                .set('month', Number(datesB[1]) - 1)
                .set('year', Number(datesB[2]));
              return dateA.isBefore(dateB) ? -1 : 1;
            } else {
              return valueA.localeCompare(valueB);
            }
          },
        },
      );
    });
    return localCols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localHeader]);

  const data: TableRow[] = React.useMemo(() => {
    if (!updatedRows.length) return [];
    return updatedRows
      .filter(
        (row) =>
          filterValue === '' ||
          row.content.toString().trim().toLowerCase().includes(filterValue.trim().toLowerCase()),
      )
      .map((row) => ({
        ...row,
        content: row.content,
        expandable: row.children ? true : false,
        onClick: row.onClick,
        id: row.id,
        background: row.background,
      }));
  }, [filterValue, updatedRows]);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: paginationConfig?.pageSize || 25,
  });

  const tableInstance = useReactTable({
    columns,
    data,
    state: {
      columnFilters,
      sorting,
      pagination: { pageIndex: paginationState.pageIndex, pageSize: paginationState.pageSize },
    },
    onPaginationChange: setPaginationState,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    autoResetPageIndex: false,
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(), //client side filtering
  });

  const updateLocalHeaderFilter = (index: number): void => {
    const workingCopy: TableHeader[] = [...localHeader];
    workingCopy[index].visible = !localHeader[index].visible;
    setLocalHeader(workingCopy);
  };

  // Update pagination on changes of the paginationConfig provided to the component
  useEffect(() => {
    const locaStorageSize: string | null = localStorage.getItem('pagesize');
    if (locaStorageSize)
      setPaginationState({
        pageIndex: paginationState.pageIndex,
        pageSize: Number(locaStorageSize),
      });
    else if (paginationConfig?.pageSize)
      setPaginationState({
        pageIndex: paginationState.pageIndex,
        pageSize: paginationConfig.pageSize,
      });
  }, [paginationConfig]);
  // Hook to set the paginationConfig on change

  /**
   * Callback function to handle the column drag and drop
   *
   * @param dragIndex The original index of the column
   * @param hoverIndex The new target index of the column
   */
  const moveColumn = (dragIndex: number, hoverIndex: number) => {
    const draggedColumn = localHeader[dragIndex];
    const updatedColumns = [...localHeader];
    updatedColumns.splice(dragIndex, 1);
    updatedColumns.splice(hoverIndex, 0, draggedColumn);

    setLocalHeader(updatedColumns);

    const localRows: TableRow[] = updatedRows.map((row) => {
      const updatedContent = [...row.content];
      // the actual reordering
      const [movedContent] = updatedContent.splice(dragIndex, 1);
      updatedContent.splice(hoverIndex, 0, movedContent);

      return {
        ...row,
        content: updatedContent,
      };
    });

    setUpdatedRows(localRows);
  };

  /**
   * Util method to decide if a sumRow should be added
   * True if any header should show the sum
   */
  const showSum: boolean = useMemo((): boolean => {
    return header.some((head) => head.showSum);
  }, [header]);

  return (
    <div className="table-component">
      <DndProvider backend={HTML5Backend}>
        {hideTopFilter || (
          <div className="table-component__filter__wrapper">
            {!hideTopFilter && (
              <div className="table-component__filter__icon__wrapper">
                <FilterIcon
                  className="table-component__filter__icon"
                  onClick={() => toggleFilter((old) => !old)}
                />
                {updateTableConfig && localHeader && (
                  <>
                    <SaveIcon
                      className="table-component__filter__save-icon"
                      onClick={() => updateTableConfig(localHeader)}
                    />
                    {showFilter && (
                      <ResetIcon
                        className="table-component__filter__reset-icon"
                        onClick={() => updateTableConfig(undefined)}
                      />
                    )}
                  </>
                )}
              </div>
            )}
            {selectAll && (
              <Checkbox label={selectAllLabel} onCheck={selectAll} isChecked={allSelecteded} />
            )}
            {showFilter && (
              <div className="table-component__filter__entry__wrapper">
                {localHeader.map((head, index) => (
                  <div
                    className="table-component__filter__entry"
                    key={`table-component-header-${head}-${index}`}
                  >
                    <Checkbox
                      isChecked={head.visible || false}
                      onCheck={() => updateLocalHeaderFilter(index)}
                      label={head.text}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <table>
          <thead style={{ display: headless ? 'none' : '' }}>
            {tableInstance.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(
                  (column: any, index: number) =>
                    localHeader[index]?.visible && (
                      <DraggableColumnHeader
                        key={column.id}
                        column={column}
                        index={index}
                        moveColumn={moveColumn}
                      />
                    ),
                )}
              </tr>
            ))}
          </thead>
          <tbody>
            {tableInstance.getRowModel().rows.map((row: any) => {
              return (
                <React.Fragment key={row.id}>
                  <tr onClick={() => row.original.onClick?.()} key={`tr-${row.original.id}`}>
                    {row
                      .getVisibleCells()
                      .filter((_: any, index: number) => localHeader[index]?.visible)
                      .map((cell: any, index: number) => {
                        return (
                          <td
                            style={{
                              flexGrow:
                                (localHeader[index]?.growFactor &&
                                  localHeader[index].growFactor) !== 0
                                  ? localHeader[index].growFactor
                                  : '1',
                              cursor: row.original.onClick ? 'pointer' : '',
                              backgroundColor:
                                filterValue !== '' &&
                                cell
                                  .getValue()
                                  ?.toString()
                                  ?.toLocaleLowerCase()
                                  .includes(filterValue.toLocaleLowerCase())
                                  ? '#ffdd33'
                                  : row.original.background
                                    ? row.original.background
                                    : '',
                            }}
                            key={`original-id-${row.original.id}-${index}`}
                          >
                            <div className="table-component__cell">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              {row.original.actions &&
                              row.original.actions.length === 0 &&
                              row.original.children &&
                              index === row.original.content.length ? (
                                <ChevronDown
                                  className={`table-component__sorting-indicator ${
                                    visibleChilds?.includes(row.original.id) ? 'up' : 'side'
                                  }`}
                                  onClick={() =>
                                    setVisibleChilds &&
                                    setVisibleChilds((prev) => addOrRemove(prev, row.original.id))
                                  }
                                />
                              ) : row.original.actions &&
                                row.original.children &&
                                index === row.original.content.length ? (
                                <div className="table-component__action-wrapper">
                                  {...row.original.actions}
                                  <ChevronDown
                                    className={`table-component__sorting-indicator ${
                                      visibleChilds?.includes(row.original.id) ? 'up' : 'side'
                                    }`}
                                    onClick={() =>
                                      setVisibleChilds &&
                                      setVisibleChilds((prev) => addOrRemove(prev, row.original.id))
                                    }
                                  />
                                </div>
                              ) : row.original.children && index === row.original.content.length ? (
                                <ChevronDown
                                  className={`table-component__sorting-indicator ${
                                    visibleChilds?.includes(row.original.id) ? 'up' : 'side'
                                  }`}
                                  onClick={() =>
                                    setVisibleChilds &&
                                    setVisibleChilds((prev) => addOrRemove(prev, row.original.id))
                                  }
                                />
                              ) : row.original.actions && index === row.original.content.length ? (
                                <>{...row.original.actions}</>
                              ) : (
                                ''
                              )}
                            </div>

                            {row.original.content.filter(
                              (_: any, index: number) => localHeader[index]?.visible,
                            )[index]?.length > 20 && (
                              <div className="table-component__popup">
                                <div className="table-component__popup__wrapper">
                                  <div className="table-component__popup__wrapper__content">
                                    {
                                      row.original.content.filter(
                                        (_: any, index: number) => localHeader[index]?.visible,
                                      )[index]
                                    }
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        );
                      })}
                  </tr>

                  {visibleChilds?.includes(row.original.id) && row.original.children && (
                    <tr style={{ padding: '0', margin: '0' }}>
                      <td colSpan={columns.length} style={{ padding: '0', margin: '0' }}>
                        {row.original.children}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          {showSum && (
            <tfoot>
              <tr key="sumrow-footer" className="sumrow">
                {tableInstance.getFooterGroups().flatMap((footerGroup) =>
                  footerGroup.headers.map((header, index) => {
                    return (
                      localHeader[index].visible && (
                        <td
                          key={header.id}
                          className="sumrow__cell"
                          style={{
                            flexGrow:
                              (localHeader[index]?.growFactor && localHeader[index].growFactor) !==
                              0
                                ? localHeader[index].growFactor
                                : '1',
                          }}
                        >
                          {flexRender(header.column.columnDef.footer, header.getContext())}
                        </td>
                      )
                    );
                  }),
                )}
              </tr>
            </tfoot>
          )}
        </table>
        {!hidePagination && (
          <div className="table-component__pagination-wrapper">
            <div>
              <button
                onClick={() => tableInstance.previousPage()}
                disabled={!tableInstance.getCanPreviousPage()}
              >
                Previous
              </button>
              <span>
                {` ${
                  tableInstance.getState().pagination.pageIndex + 1
                } / ${tableInstance.getPageCount()} `}
              </span>
              <button
                disabled={!tableInstance.getCanNextPage()}
                onClick={() => tableInstance.nextPage()}
              >
                Next
              </button>
            </div>
            <Dropdown
              selectedOption={`${
                paginationState.pageSize === rows.length ||
                ![10, 25, 50, 100].includes(paginationState.pageSize)
                  ? selectAllLabel || 'All'
                  : paginationState.pageSize
              }`}
              options={[
                { label: '10', value: '10' },
                { label: '25', value: '25' },
                { label: '50', value: '50' },
                { label: '100', value: '100' },
                {
                  label: selectAllLabel || 'All',
                  value: selectAllLabel || 'All',
                },
              ]}
              onChange={(value) => {
                localStorage.setItem(
                  'pagesize',
                  value === selectAllLabel || value === 'All' ? String(rows.length) : value,
                );
                setPaginationState({
                  ...paginationState,
                  pageSize:
                    value === selectAllLabel || value === 'All' ? rows.length : Number(value),
                });
              }}
            />
          </div>
        )}
      </DndProvider>
    </div>
  );
};

const DraggableColumnHeader: React.FC<{
  column: any;
  index: number;
  moveColumn: (dragIndex: number, hoverIndex: number) => void;
}> = ({ column, index, moveColumn }) => {
  const ref = React.useRef<HTMLTableCellElement>(null);

  const [, drop] = useDrop({
    accept: COLUMN_TYPE,
    hover(item: DragItem) {
      if (!ref.current) {
        return;
      }
      const dragIndex: number = item.index;
      const hoverIndex: number = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      moveColumn(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: COLUMN_TYPE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <th
      ref={ref}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        flexGrow: column.column.columnDef.size !== 0 ? column.column.columnDef.size : '1',
      }}
    >
      <div
        className="table-component__head-wrapper"
        onClick={column.column.getToggleSortingHandler()}
      >
        {flexRender(column.column.columnDef.header, column.getContext())}
        {{
          asc: ' 🔼',
          desc: ' 🔽',
        }[column.column.getIsSorted() as string] ?? null}
      </div>
      {column.column.getCanFilter() && (
        <div className="table-component__filter-wrapper">
          <Filter column={column.column} />
        </div>
      )}
    </th>
  );
};
